import type {
  LocalFile,
  RetrieveThreadResponse,
  SocketThreadUpdate,
  ThreadAttachment,
  ThreadMessage,
  ThreadRequestType,
} from '@frontend/api-types';
import {
  convertDateLikeToDateTimeISOString,
  dayjs,
  debounce,
  deserializeJson,
  identity,
  match,
  P,
  reverse,
  serializeJson,
} from '@frontend/duck-tape';
import * as ArrayHelpers from '@frontend/duck-tape/array/array';
import { useEffect, useListState, useState } from '@frontend/react';
import type { getSocketClient } from '@frontend/sockets';

export type ThreadInputMessageState = {
  current: string;
  inFlight: string;
};

export type UseThreadChatProps = {
  convertFilesToThreadAttachments?: (files: LocalFile[]) => Promise<ThreadAttachment[]>;
  getImageKitImageUrl?: (externalPath: string) => string;
  onBlurTextInput: () => void;
  onReceiveMessageSocketUpdate?: () => void;
  onSendFollowUpMessage?: (data: { files?: LocalFile[]; message: string; requestType: ThreadRequestType }) => void;
  refetchThread: () => Promise<void>;
  scrollToBottom: () => void;
  setInputMessage: SetStateFn<ThreadInputMessageState>;
  setThreadFiles: SetStateFn<LocalFile[]>;
  socket: ReturnType<typeof getSocketClient>;
  threadData: MaybeUndefined<RetrieveThreadResponse>;
  threadId: MaybeUndefined<string>;
  token: MaybeNil<string>;
};

export const useThreadChat = ({
  convertFilesToThreadAttachments,
  getImageKitImageUrl,
  onBlurTextInput,
  onReceiveMessageSocketUpdate,
  onSendFollowUpMessage,
  refetchThread,
  scrollToBottom,
  setInputMessage,
  setThreadFiles,
  socket: { disconnectSocket, getSocket, initializeSocket },
  threadData,
  token,
}: UseThreadChatProps) => {
  const [messages, { prepend: prependMessage, setState: setMessages }] = useListState<ThreadMessage>([]);
  const [title, setTitle] = useState('');
  const [isSocketInitialized, setIsSocketInitialized] = useState(false);
  const [isAIStreaming, setIsAIStreaming] = useState(false);
  const [isStreamingError, setIsStreamingError] = useState(false);
  // This value will only ever be set when we're creating a thread after the first message
  const [threadRequestType, setThreadRequestType] = useState<MaybeUndefined<ThreadRequestType>>();

  // Intentionally not resetting the socket as that can cause race conditions since sockets are globally shared
  const resetState = () => {
    setMessages([]);
    setTitle('');
    setIsAIStreaming(false);
    setIsStreamingError(false);
    setThreadRequestType(undefined);
  };

  const updateTitle = (title: string) => {
    setTitle(title);
  };

  const handleMessageSocketUpdate = (data: SocketThreadUpdate) =>
    match(data)
      // Noop
      .with({ type: P.union('search_thread_request_type', 'search_thread_title') }, identity)
      .with({ type: P.union('task_suggestions', 'text') }, (data) => {
        onReceiveMessageSocketUpdate?.();
        // must use functional update to ensure we always see most up-to-date version
        setMessages((current) => {
          if (current[0]?.type === data.type && current[0].role === data.role) {
            return [{ ...current[0], ...data }, ...current.slice(1)];
          }

          return ArrayHelpers.prepend(current, {
            ...data,
            createdAt: convertDateLikeToDateTimeISOString(dayjs().toISOString()),
          });
        });
      })
      .exhaustive();

  const connectAndEmitSocketEvent = ({
    attachments,
    messageToSend,
    threadId,
    token,
  }: {
    attachments?: ThreadAttachment[];
    messageToSend: string;
    threadId: string;
    token: string;
  }) => {
    if (getSocket('researchThread')) {
      disconnectSocket();
    }
    if (threadId) {
      const socket = initializeSocket({
        channelType: 'researchThread',
        params: { id: threadId },
        token,
      });

      const onConnect = () => {
        setIsSocketInitialized(true);
        // TODO (ethan): Account for this comment?
        // Checking for messages.length accounts for the case where the user navigates to a
        // thread with the example still in the search params
        setIsAIStreaming(true);
        socket.emit(
          'generate_search_response',
          serializeJson({ attachments, requestText: messageToSend, searchThreadId: threadId, v2: true }),
        );
      };

      const onDisconnect = () => {
        setIsAIStreaming(false);
      };

      const onGenerationCompleted = () => {
        setIsAIStreaming(false);
        disconnectSocket();
        setIsSocketInitialized(false);
        // Hack for updating the cache
        refetchThread();
      };

      const onGenerationFailed = () => {
        setIsAIStreaming(false);
        setIsStreamingError(true);
        disconnectSocket();
        setIsSocketInitialized(false);
      };

      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('generation_completed', onGenerationCompleted);
      socket.on('generation_failed', onGenerationFailed);
      return socket;
    }
    return undefined;
  };

  const interruptStream = () => {
    setInputMessage((prev) => ({ ...prev, current: prev.inFlight }));
    setIsAIStreaming(false);
    const socket = getSocket('researchThread');
    if (socket && isSocketInitialized && token) {
      socket.emit('stop_generation');
      // Since this socket emission will have some latency, we should re-initialize the socket
      // to drop any lagging results
      disconnectSocket();
    }
  };

  const prependMessagesForAIResponse = (message: string, attachments?: ThreadAttachment[]) => {
    prependMessage(
      // Prepending this message to the list so that the user knows their message is being processed
      {
        content: '',
        createdAt: convertDateLikeToDateTimeISOString(dayjs().toISOString()),
        role: 'ai_bot',
        type: 'text',
      },
      {
        attachments:
          getImageKitImageUrl &&
          attachments?.map(({ contentType, externalPath }) => ({
            contentType,
            fileUrl: getImageKitImageUrl(externalPath),
          })),
        content: message,
        // createdAt must be unique
        createdAt: convertDateLikeToDateTimeISOString(dayjs().add(1, 'ms').toISOString()),
        role: 'client',
        type: 'text',
      },
    );
  };

  const sendThreadMessage = async (message: string, files: LocalFile[], { threadId }: { threadId: string }) => {
    if (!token || !threadId) return;

    const attachments: ThreadAttachment[] = await (files.length > 0 && convertFilesToThreadAttachments
      ? convertFilesToThreadAttachments(files)
      : []);

    if (threadRequestType && onSendFollowUpMessage) {
      onSendFollowUpMessage?.({ files, message, requestType: threadRequestType });
    } else {
      // Since threadRequestType only gets set when we're creating a thread,
      // we can assume that if it's not set, we should just send messages as normal
      setIsStreamingError(false);
      onBlurTextInput();
      prependMessagesForAIResponse(message, attachments);
      connectAndEmitSocketEvent({
        attachments,
        messageToSend: message,
        threadId,
        token,
      });
      setInputMessage({ current: '', inFlight: message });
      setThreadFiles([]);
    }
  };

  // Effects
  useEffect(() => {
    if (threadData?.messages) {
      setMessages(reverse(threadData.messages));
      updateTitle(threadData.title ?? '');
      scrollToBottom();
    }
  }, [threadData]);

  useEffect(() => {
    const socket = getSocket('researchThread');
    if (socket && isSocketInitialized) {
      const handleSocketUpdate = debounce(
        (json: SerializedJson<SocketThreadUpdate>) => {
          const data = deserializeJson(json);
          match(data)
            .with({ type: 'search_thread_title' }, (message) => {
              updateTitle(message.content);
            })
            .with({ type: 'task_suggestions' }, (data) => handleMessageSocketUpdate(data))
            .with({ type: 'search_thread_request_type' }, (data) => {
              setThreadRequestType(data.content);
            })
            .with({ type: 'text' }, (data) => handleMessageSocketUpdate(data))
            .exhaustive();
        },
        10,
        { maxWait: 100 },
      );

      socket.on('update_search_thread', handleSocketUpdate);
      return () => {
        socket.off('update_search_thread', handleSocketUpdate);
      };
    }
    return identity;
  }, [messages, isSocketInitialized]);

  return {
    interruptStream,
    isAIStreaming,
    isStreamingError,
    messages,
    resetState,
    sendThreadMessage,
    slideupLinksData: {
      selectedText: undefined,
      siteData: [],
    },
    threadRequestType,
    title,
  };
};
